/* ============================================================
   LOOMÉ — theme.js
   One deferred script. Same motion engine as the approved
   skeleton (buildLoom / runShuttle / initReveals / initHeader /
   initBurger), extended for commerce: AJAX cart drawer, quick-view
   modal, variant sync, qty steppers, newsletter/contact validation.

   Performance rules kept tight for Core Web Vitals:
   · one passive rAF-throttled scroll listener (no layout thrash)
   · IntersectionObserver for reveals (no per-frame work)
   · all motion gated behind prefers-reduced-motion
   · commerce uses Shopify's AJAX Cart API; degrades gracefully to
     normal form POST when JS/endpoints are unavailable (static mirror)
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var money = (window.LOOME && window.LOOME.moneyFormat) || '{{amount}}';
  var strings = (window.LOOME && window.LOOME.strings) || {};
  var routes = (window.LOOME && window.LOOME.routes) || {};

  /* ---------- helpers ---------- */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  // Format cents into the shop's money format (mirrors Shopify's money filter well enough for display)
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    function defaultTo(dec, thou) {
      dec = dec == null ? 2 : dec; thou = thou || ',';
      var n = (cents / 100).toFixed(dec);
      var parts = n.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thou);
      return dollars + (parts[1] ? '.' + parts[1] : '');
    }
    var match = money.match(placeholderRegex);
    var token = match ? match[1] : 'amount';
    switch (token) {
      case 'amount': value = defaultTo(2); break;
      case 'amount_no_decimals': value = defaultTo(0); break;
      case 'amount_with_comma_separator': value = defaultTo(2, '.'); break;
      case 'amount_no_decimals_with_comma_separator': value = defaultTo(0, '.'); break;
      default: value = defaultTo(2);
    }
    return money.replace(placeholderRegex, value);
  }

  /* ---------- 1. Build the loom (warp threads + weft weave) ---------- */
  function buildLoom() {
    var warp = document.getElementById('warp');
    var weft = document.getElementById('weft');
    if (!warp || !weft) return;

    var W = 400, H = 500;
    var warpCount = 22, weftCount = 16;

    var frag = '';
    for (var i = 0; i < warpCount; i++) {
      var x = (W / (warpCount - 1)) * i;
      frag += '<line x1="' + x.toFixed(1) + '" y1="0" x2="' + x.toFixed(1) + '" y2="' + H + '"/>';
    }
    warp.innerHTML = frag;

    var wfrag = '';
    for (var j = 0; j < weftCount; j++) {
      var y = H - (H / weftCount) * (j + 0.5);
      wfrag += '<line class="weft-anim" x1="0" y1="' + y.toFixed(1) + '" x2="' + W + '" y2="' + y.toFixed(1) +
               '" stroke-dasharray="' + W + '" stroke-dashoffset="' + (reduceMotion ? 0 : W) + '"' +
               ' style="' + (reduceMotion ? '' : 'transition:stroke-dashoffset 1.1s cubic-bezier(.22,.61,.36,1) ' + (j * 0.14).toFixed(2) + 's') + '"/>';
    }
    weft.innerHTML = wfrag;

    var mono = document.getElementById('monogram');
    if (reduceMotion) {
      if (mono) mono.setAttribute('opacity', '0.9');
      return;
    }

    var frame = weft.closest('.loom-frame') || weft;
    var started = false;
    function startWeave() {
      if (started) return; started = true;
      var lines = weft.querySelectorAll('.weft-anim');
      void weft.getBoundingClientRect().width;
      requestAnimationFrame(function () {
        for (var k = 0; k < lines.length; k++) lines[k].setAttribute('stroke-dashoffset', '0');
      });
      runShuttle(weftCount);
      if (mono) {
        setTimeout(function () {
          mono.style.transition = 'opacity 1.2s ease';
          mono.setAttribute('opacity', '0.9');
        }, 1100 + weftCount * 140 + 200);
      }
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) { startWeave(); io.disconnect(); } });
      }, { threshold: 0.25 });
      io.observe(frame);
    } else {
      startWeave();
    }
  }

  /* ---------- 2. Shuttle — travels across as the weft builds ---------- */
  function runShuttle(rows) {
    var shuttle = document.getElementById('shuttle');
    if (!shuttle) return;
    var H = 500, row = 0, dir = 1;
    shuttle.setAttribute('opacity', '0.9');
    shuttle.setAttribute('cx', '200');
    function step() {
      if (row >= rows) { shuttle.setAttribute('opacity', '0'); return; }
      var y = H - (H / rows) * (row + 0.5);
      var startX = dir > 0 ? 200 : -200, endX = dir > 0 ? -200 : 200;
      shuttle.setAttribute('cy', y.toFixed(1));
      shuttle.style.transform = 'translateX(' + startX + 'px)';
      shuttle.style.transition = 'none';
      requestAnimationFrame(function () {
        shuttle.style.transition = 'transform 0.9s linear';
        shuttle.style.transform = 'translateX(' + endX + 'px)';
      });
      dir *= -1; row++;
      setTimeout(step, 140);
    }
    step();
  }

  /* ---------- 3. Reveal-on-scroll ---------- */
  function initReveals() {
    var els = $all('.reveal');
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- 4. Header: transparent -> solid (single passive listener) ---------- */
  function initHeader() {
    var header = document.getElementById('header');
    if (!header) return;
    var ticking = false;
    function update() {
      if (window.pageYOffset > 60) header.classList.add('is-solid');
      else header.classList.remove('is-solid');
      ticking = false;
    }
    update();
    window.addEventListener('scroll', function () {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
  }

  /* ---------- 5. Mobile nav ---------- */
  function initBurger() {
    var burger = document.getElementById('burger');
    var links = document.querySelector('.nav-links');
    if (!burger || !links) return;
    function setOpen(open) {
      links.classList.toggle('open', open);
      burger.setAttribute('aria-expanded', String(open));
    }
    burger.addEventListener('click', function () {
      setOpen(!links.classList.contains('open'));
    });
    links.addEventListener('click', function (e) {
      if (e.target.tagName === 'A') setOpen(false);
    });
  }

  /* ---------- 6. Newsletter + contact validation ---------- */
  function initInteractions() {
    var form = document.getElementById('newsForm');
    var note = document.getElementById('newsNote');
    if (form && note) {
      var EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      // On a live Shopify store the form POSTs to /contact#... ; only client-validate.
      var isShopifyForm = form.getAttribute('action') && form.getAttribute('action').indexOf('/contact') !== -1;
      form.addEventListener('submit', function (e) {
        var email = form.querySelector('input[type="email"], input');
        var ok = email && EMAIL.test(email.value.trim());
        if (!ok) {
          e.preventDefault();
          note.textContent = strings.emailInvalid || 'Please enter a valid email address.';
          note.classList.remove('is-ok'); note.classList.add('is-err');
          if (email) { email.setAttribute('aria-invalid', 'true'); email.focus(); }
          return;
        }
        email.removeAttribute('aria-invalid');
        if (!isShopifyForm) {
          e.preventDefault();
          note.textContent = strings.newsThanks || 'Thank you — welcome to the LOOMÉ family.';
          note.classList.remove('is-err'); note.classList.add('is-ok');
          form.reset();
        }
        // On Shopify: let it POST; the success/error is rendered server-side after redirect.
      });
    }
  }

  /* ---------- 7. Quantity steppers (PDP, cart, drawer via delegation) ---------- */
  function clampQty(input) {
    var v = parseInt(input.value, 10);
    if (isNaN(v) || v < 1) v = 1;
    var max = parseInt(input.getAttribute('max'), 10);
    if (!isNaN(max) && max > 0 && v > max) v = max;
    input.value = String(v);
    return v;
  }
  function initQtySteppers() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-qty]');
      if (!btn) return;
      var wrap = btn.closest('.qty');
      var input = wrap && wrap.querySelector('input');
      if (!input) return;
      var v = parseInt(input.value, 10) || 1;
      v += btn.getAttribute('data-qty') === 'up' ? 1 : -1;
      input.value = String(v);
      clampQty(input);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    $all('.qty input').forEach(function (input) {
      input.addEventListener('change', function () { clampQty(input); });
    });
  }

  /* ---------- 8. Variant selection (PDP + modal) ---------- */
  function initVariantForm(root) {
    root = root || document;
    $all('[data-product-form]', root).forEach(function (form) {
      var dataEl = document.getElementById(form.getAttribute('data-variants'));
      if (!dataEl) return;
      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (err) { return; }

      var idInput = form.querySelector('[name="id"]');
      var priceEl = form.querySelector('[data-price]');
      var compareEl = form.querySelector('[data-compare]');
      var addBtn = form.querySelector('[data-add]');
      var addLabel = addBtn ? addBtn.querySelector('[data-add-label]') : null;
      var mediaHost = document.querySelector('[data-media-for="' + form.getAttribute('data-media-target') + '"]') ||
                      document.querySelector('.pdp-main-media');

      function currentSelections() {
        var picks = [];
        $all('[data-option-index]', form).forEach(function (group) {
          var checked = group.querySelector('input:checked');
          if (checked) picks[parseInt(group.getAttribute('data-option-index'), 10)] = checked.value;
        });
        return picks;
      }
      function findVariant(picks) {
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i], match = true;
          for (var o = 0; o < picks.length; o++) {
            if (picks[o] != null && v.options[o] !== picks[o]) { match = false; break; }
          }
          if (match) return v;
        }
        return null;
      }
      function updateChosenLabels() {
        $all('[data-option-index]', form).forEach(function (group) {
          var checked = group.querySelector('input:checked');
          var out = group.parentNode.querySelector('.chosen');
          if (checked && out) out.textContent = checked.value;
        });
      }
      function render() {
        var picks = currentSelections();
        var v = findVariant(picks);
        updateChosenLabels();
        if (!v) {
          if (addBtn) { addBtn.setAttribute('disabled', ''); }
          if (addLabel) addLabel.textContent = strings.unavailable || 'Unavailable';
          return;
        }
        if (idInput) idInput.value = v.id;
        if (priceEl) priceEl.textContent = formatMoney(v.price);
        if (compareEl) {
          if (v.compare_at_price && v.compare_at_price > v.price) {
            compareEl.textContent = formatMoney(v.compare_at_price);
            compareEl.hidden = false;
          } else { compareEl.hidden = true; }
        }
        if (addBtn) {
          if (v.available) {
            addBtn.removeAttribute('disabled');
            if (addLabel) addLabel.textContent = strings.addToCart || 'Add to cart';
          } else {
            addBtn.setAttribute('disabled', '');
            if (addLabel) addLabel.textContent = strings.soldOut || 'Sold out';
          }
        }
        // swap main media to the variant's featured image if present
        if (v.featured_media && mediaHost) {
          var target = mediaHost.querySelector('img[data-media-id="' + v.featured_media.id + '"]');
          if (target) activateMedia(mediaHost, target);
        }
      }
      $all('[data-option-index] input', form).forEach(function (input) {
        input.addEventListener('change', render);
      });
      render();
    });
  }

  function activateMedia(host, img) {
    $all('img', host).forEach(function (m) { m.classList.remove('is-active'); });
    img.classList.add('is-active');
    var id = img.getAttribute('data-media-id');
    var thumbs = document.querySelector('.pdp-thumbs');
    if (thumbs && id) {
      $all('.pdp-thumb', thumbs).forEach(function (t) {
        t.classList.toggle('is-active', t.getAttribute('data-media-id') === id);
      });
    }
  }

  /* ---------- 9. PDP gallery thumbs ---------- */
  function initGallery() {
    var main = document.querySelector('.pdp-main-media');
    var thumbs = document.querySelector('.pdp-thumbs');
    if (!main || !thumbs) return;
    // ensure one image is active
    if (!main.querySelector('img.is-active')) {
      var first = main.querySelector('img');
      if (first) first.classList.add('is-active');
    }
    thumbs.addEventListener('click', function (e) {
      var t = e.target.closest('.pdp-thumb');
      if (!t) return;
      var id = t.getAttribute('data-media-id');
      var target = main.querySelector('img[data-media-id="' + id + '"]');
      if (target) activateMedia(main, target);
    });
  }

  /* ---------- 10. Cart drawer + AJAX cart ---------- */
  var Cart = (function () {
    var overlay, drawer, countEls, hasDrawer;
    function init() {
      overlay = document.getElementById('overlay');
      drawer = document.getElementById('cartDrawer');
      countEls = $all('.cart-count');
      hasDrawer = !!drawer;
    }
    function setCount(n) {
      countEls.forEach(function (el) {
        el.textContent = String(n);
        el.hidden = n <= 0;
      });
    }
    function open() {
      if (!drawer) return;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      if (overlay) overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      var close = drawer.querySelector('.drawer-close');
      if (close) close.focus();
    }
    function close() {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      if (overlay && !isModalOpen()) overlay.classList.remove('is-open');
      if (!isModalOpen()) document.body.style.overflow = '';
    }
    function toast(title) {
      var t = document.getElementById('toast');
      if (!t) return;
      t.innerHTML = (strings.added || 'Added') + ' — <b>' + title + '</b>';
      t.classList.add('is-show');
      clearTimeout(t._timer);
      t._timer = setTimeout(function () { t.classList.remove('is-show'); }, 2200);
    }
    function fetchState() {
      if (!routes.cart) return Promise.resolve(null);
      return fetch(routes.cart + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    function renderDrawer(cart) {
      if (!drawer || !cart) return;
      setCount(cart.item_count);
      var body = drawer.querySelector('.drawer-body');
      var foot = drawer.querySelector('.drawer-foot');
      if (!body) return;
      if (!cart.items || !cart.items.length) {
        body.innerHTML = '<div class="drawer-empty"><h3 class="serif">' + (strings.cartEmpty || 'Your cart is empty') +
          '</h3><p>' + (strings.cartEmptyBody || 'Fine linen, chosen slowly.') + '</p></div>';
        if (foot) foot.hidden = true;
        return;
      }
      if (foot) foot.hidden = false;
      var html = '<ul class="drawer-lines">';
      cart.items.forEach(function (item) {
        var img = item.image
          ? '<img src="' + item.image.replace(/(_\d+x\d+)?\.(jpg|jpeg|png|webp|gif)/i, '_160x.$2') + '" alt="" width="70" height="93" loading="lazy">'
          : '<div class="ph"></div>';
        var variant = (item.variant_title && item.variant_title !== 'Default Title') ? '<div class="drawer-line-variant">' + item.variant_title + '</div>' : '';
        html += '<li class="drawer-line" data-key="' + item.key + '">' +
          '<a class="drawer-line-media" href="' + item.url + '">' + img + '</a>' +
          '<div><a class="drawer-line-title" href="' + item.url + '">' + item.product_title + '</a>' + variant +
            '<div class="drawer-line-meta">' +
              '<div class="drawer-qty" data-key="' + item.key + '">' +
                '<button type="button" data-line-qty="down" aria-label="' + (strings.decrease || 'Decrease') + '">&minus;</button>' +
                '<span>' + item.quantity + '</span>' +
                '<button type="button" data-line-qty="up" aria-label="' + (strings.increase || 'Increase') + '">+</button>' +
              '</div>' +
              '<button type="button" class="drawer-line-remove" data-remove="' + item.key + '">' + (strings.remove || 'Remove') + '</button>' +
            '</div>' +
          '</div>' +
          '<div class="drawer-line-price">' + formatMoney(item.final_line_price) + '</div>' +
        '</li>';
      });
      html += '</ul>';
      body.innerHTML = html;
      var sub = drawer.querySelector('.drawer-subtotal b');
      if (sub) sub.textContent = formatMoney(cart.total_price);
    }
    function renderCartPage(cart) {
      var form = document.querySelector('[data-cart-form]');
      if (!form || !cart) return;
      if (!cart.items || !cart.items.length) { window.location.reload(); return; }
      var byKey = {};
      cart.items.forEach(function (it) { byKey[it.key] = it; });
      $all('.cart-line', form).forEach(function (line) {
        var key = line.getAttribute('data-key');
        var it = byKey[key];
        if (!it) { line.parentNode.removeChild(line); return; }
        var price = line.querySelector('.cart-line-price');
        if (price) price.textContent = formatMoney(it.final_line_price);
        var input = line.querySelector('[data-line-input]');
        if (input && document.activeElement !== input) input.value = it.quantity;
      });
      var sub = document.querySelector('[data-cart-subtotal]');
      if (sub) sub.textContent = formatMoney(cart.total_price);
      var tot = document.querySelector('[data-cart-total]');
      if (tot) tot.textContent = formatMoney(cart.total_price);
    }
    function refresh() {
      return fetchState().then(function (cart) { if (cart) { renderDrawer(cart); renderCartPage(cart); } return cart; });
    }
    function change(key, quantity) {
      if (!routes.cartChange) return Promise.resolve(null);
      if (drawer) drawer.classList.add('is-loading');
      return fetch(routes.cartChange + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      }).then(function (r) { return r.json(); }).then(function (cart) {
        renderDrawer(cart);
        renderCartPage(cart);
        if (drawer) drawer.classList.remove('is-loading');
        return cart;
      }).catch(function () { if (drawer) drawer.classList.remove('is-loading'); });
    }
    function add(form) {
      var formData = new FormData(form);
      var btn = form.querySelector('[data-add]');
      var label = btn ? btn.querySelector('[data-add-label]') : null;
      var prev = label ? label.textContent : '';
      if (label) label.textContent = strings.adding || 'Adding…';
      if (btn) btn.setAttribute('disabled', '');
      return fetch(routes.cartAdd + '.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData
      }).then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
        .then(function (res) {
          if (btn) btn.removeAttribute('disabled');
          if (!res.ok) {
            if (label) label.textContent = res.data.message || (strings.error || 'Error');
            setTimeout(function () { if (label) label.textContent = prev; }, 1800);
            return;
          }
          if (label) label.textContent = strings.addedShort || 'Added ✓';
          setTimeout(function () { if (label) label.textContent = prev; }, 1600);
          return refresh().then(function (cart) {
            if (hasDrawer) {
              // If the add came from the quick-view modal, close it first so the
              // drawer (lower z-index) isn't hidden behind it. QuickView.close()
              // leaves the overlay + scroll-lock intact while the drawer is open.
              if (isModalOpen()) QuickView.close();
              open();
            } else {
              toast(res.data.product_title || res.data.title || '');
            }
            return cart;
          });
        }).catch(function () {
          if (btn) btn.removeAttribute('disabled');
          if (label) label.textContent = prev;
        });
    }
    return {
      init: init, open: open, close: close, refresh: refresh,
      change: change, add: add, setCount: setCount, toast: toast, hasDrawer: function () { return hasDrawer; }
    };
  })();

  function isModalOpen() { var m = document.getElementById('quickModal'); return m && m.classList.contains('is-open'); }

  function initCart() {
    Cart.init();

    // open drawer from header cart button (only if a drawer exists)
    var cartBtn = document.getElementById('cartBtn');
    if (cartBtn && Cart.hasDrawer()) {
      cartBtn.addEventListener('click', function (e) {
        e.preventDefault();
        Cart.refresh().then(Cart.open);
      });
    }
    // close controls
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-cart-close]')) { e.preventDefault(); Cart.close(); }
    });
    var overlay = document.getElementById('overlay');
    if (overlay) overlay.addEventListener('click', function () { Cart.close(); QuickView.close(); });

    // add-to-cart forms (PDP, featured, quick-view) — progressive enhancement
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-product-form]');
      if (!form) return;
      if (!routes.cartAdd) return; // static mirror: allow normal behaviour / no-op
      e.preventDefault();
      Cart.add(form);
    });

    // drawer line qty + remove (delegated)
    document.addEventListener('click', function (e) {
      var q = e.target.closest('[data-line-qty]');
      if (q) {
        var wrap = q.closest('.drawer-qty');
        var key = wrap.getAttribute('data-key');
        var span = wrap.querySelector('span');
        var cur = parseInt(span.textContent, 10) || 1;
        cur += q.getAttribute('data-line-qty') === 'up' ? 1 : -1;
        if (cur < 0) cur = 0;
        Cart.change(key, cur);
        return;
      }
      var rm = e.target.closest('[data-remove]');
      if (rm && !rm.closest('[data-cart-form]')) { Cart.change(rm.getAttribute('data-remove'), 0); }
    });

    // cart PAGE line quantity changes (input + steppers dispatch 'change')
    var cartForm = document.querySelector('[data-cart-form]');
    if (cartForm && routes.cartChange) {
      cartForm.addEventListener('change', function (e) {
        var input = e.target.closest('[data-line-input]');
        if (!input) return;
        var line = input.closest('.cart-line');
        if (!line) return;
        var key = line.getAttribute('data-key');
        var qty = parseInt(input.value, 10);
        if (isNaN(qty) || qty < 0) qty = 0;
        Cart.change(key, qty);
      });
      cartForm.addEventListener('click', function (e) {
        var rm = e.target.closest('.cart-line-remove[data-remove]');
        if (!rm) return;
        e.preventDefault();
        Cart.change(rm.getAttribute('data-remove'), 0);
      });
    }

    // initial count sync on load (live store only)
    if (routes.cart) Cart.refresh();
  }

  /* ---------- 11. Quick-view modal ---------- */
  var QuickView = (function () {
    var modal, dialog, overlay, lastFocus;
    function ensure() {
      modal = document.getElementById('quickModal');
      overlay = document.getElementById('overlay');
      dialog = modal ? modal.querySelector('.modal-dialog') : null;
    }
    function open(html) {
      ensure();
      if (!modal || !dialog) return;
      lastFocus = document.activeElement;
      var body = modal.querySelector('.modal-content');
      if (body) body.innerHTML = html;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      if (overlay) overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      initVariantForm(modal);
      // Defer focus one frame: the modal transitions from visibility:hidden,
      // and an element that is still computed visibility:hidden on this frame
      // cannot receive focus. rAF runs after the is-open style is committed.
      var close = modal.querySelector('.modal-close');
      if (close) requestAnimationFrame(function () { close.focus(); });
    }
    function close() {
      ensure();
      if (!modal) return;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      var drawerOpen = document.getElementById('cartDrawer');
      drawerOpen = drawerOpen && drawerOpen.classList.contains('is-open');
      if (overlay && !drawerOpen) overlay.classList.remove('is-open');
      if (!drawerOpen) document.body.style.overflow = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
    function loadFrom(handle, trigger) {
      // fetch the product's quick-view section rendered by Shopify
      if (routes.root == null || !window.fetch) return;
      var url = (routes.root === '/' ? '' : routes.root) + '/products/' + handle + '?view=quickview';
      if (trigger) trigger.setAttribute('aria-busy', 'true');
      fetch(url).then(function (r) { return r.text(); }).then(function (text) {
        if (trigger) trigger.removeAttribute('aria-busy');
        // extract the modal-ready markup between the sentinels
        var m = text.match(/<!--QV-START-->([\s\S]*?)<!--QV-END-->/);
        open(m ? m[1] : text);
      }).catch(function () { if (trigger) trigger.removeAttribute('aria-busy'); });
    }
    return { open: open, close: close, loadFrom: loadFrom };
  })();

  function initQuickView() {
    document.addEventListener('click', function (e) {
      var t = e.target.closest('[data-quickview]');
      if (!t) return;
      e.preventDefault();
      var inline = t.getAttribute('data-quickview');
      if (inline) {
        // static mirror path: markup is stored in a <template>
        var tpl = document.getElementById(inline);
        if (tpl) QuickView.open(tpl.innerHTML);
      } else {
        QuickView.loadFrom(t.getAttribute('data-handle'), t);
      }
    });
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-modal-close]')) { e.preventDefault(); QuickView.close(); }
    });
  }

  /* ---------- 12. Global key handling (Esc closes overlays) ---------- */
  function initKeys() {
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { QuickView.close(); Cart.close(); }
    });
  }

  /* ---------- boot ---------- */
  function boot() {
    buildLoom();
    initReveals();
    initHeader();
    initBurger();
    initInteractions();
    initQtySteppers();
    initGallery();
    initVariantForm(document);
    initCart();
    initQuickView();
    initKeys();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
